import { FC, useEffect, useMemo, useState } from 'react';
import { Empty, Flex, Select, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { _debounce } from '@medicusunion/front-utils';
import { fetchParties } from 'services/api/invoicing';
import { DATE_FORMAT } from 'constants/dates';
import { PartyKind, PartyResult } from 'types/api/invoicing';

interface IProps {
  value?: string;
  onChange?: (value?: string) => void;
  onSelectParty?: (party: PartyResult | null) => void;
}

const partyLabel = (party: PartyResult) =>
  `${party.firstName} ${party.lastName ?? ''}`.trim() || '—';

const KIND_LABEL: Record<PartyKind, string> = {
  [PartyKind.Patient]: 'Patient',
  [PartyKind.Customer]: 'Customer'
};

/**
 * Searchable picker over BOTH party tables. A patient and a billing customer
 * are both valid invoice recipients, so they are searched together — picking
 * from one combined list is what stops the same person being created twice.
 *
 * `value` is the party's `ref` namespaced by kind (`patient:12`), because a
 * numeric patient id and a customer UUID can otherwise collide in the Select.
 */
const PartySelect: FC<IProps> = ({ value, onChange, onSelectParty }) => {
  const debounce = useMemo(() => _debounce(), []);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PartyResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchParties(query);

        if (!cancelled) setRows(res);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const options = useMemo(
    () =>
      rows.map(party => {
        const details = [
          party.email,
          party.dateOfBirth
            ? dayjs(party.dateOfBirth).format(DATE_FORMAT)
            : null
        ]
          .filter(Boolean)
          .join(' · ');

        return {
          value: `${party.kind}:${party.ref}`,
          // Plain text so the closed Select shows a readable value.
          label: partyLabel(party),
          title: details,
          party,
          // antd renders this in the dropdown when present.
          optionLabel: (
            <Flex align="center" gap={8} wrap="wrap">
              <span>{partyLabel(party)}</span>
              <Tag
                color={party.kind === PartyKind.Patient ? 'blue' : 'default'}
              >
                {KIND_LABEL[party.kind]}
              </Tag>
              {!!party.duplicateOf && (
                <Tag color="warning">
                  {`Also a ${KIND_LABEL[party.duplicateOf].toLowerCase()}`}
                </Tag>
              )}
              {!!details && (
                <Typography.Text type="secondary">{details}</Typography.Text>
              )}
            </Flex>
          )
        };
      }),
    [rows]
  );

  const handleChange = (next?: string) => {
    onChange?.(next);
    onSelectParty?.(
      next ? options.find(o => o.value === next)?.party ?? null : null
    );
  };

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      placeholder="Search a patient or customer — name, email, phone or birthday"
      value={value}
      options={options}
      optionRender={option => option.data.optionLabel}
      loading={loading}
      onChange={handleChange}
      onSearch={input => debounce(() => setQuery(input))}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={query ? 'No match' : 'Type to search'}
          />
        )
      }
    />
  );
};

export default PartySelect;
